/**
 * The Billing tab's toolbar (Aaron, step 21 item 5.1) — the Build tab's, in
 * the same place and the same sizes: a 330 × 38 search, then the accent
 * **New billing plan ▾** where Build has **Add visit ▾**, and the two pills
 * on the right, Prices & costs and Collapse all, doing on this tab what they
 * do on that one.
 *
 * Adding a plan used to be a dashed card at the foot of the list, so the two
 * tabs of one builder had two different ways to add the thing each is about.
 * The menu names the five kinds, so a plan is made as the kind it is meant to
 * be instead of a generic one somebody then has to change.
 */
import { useRef, useState } from 'react'
import { BILLING_KIND_CARDS, BILLING_PLAN_KINDS, type BillingPlanKind } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { Popover } from '../../components/overlays.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { IconChevronDown, IconPlus, IconSearch } from '../../frame/icons.js'
import { NEW_BILLING_PLAN, NEW_PLAN_SHEET, SEARCH_BILLING_PLANS } from '../../builder/copy.js'
import { Pill } from '../quote-toolbar.js'
import { TileMenuRow } from '../add-visit-menu.js'
import { KindGlyph } from './parts.js'

export function BillingToolbar({
  canEdit, search, onSearch, onNewPlan, railOn, onRail, allCollapsed, onCollapseAll,
}: {
  canEdit: boolean
  search: string
  onSearch: (text: string) => void
  onNewPlan: (kind: BillingPlanKind) => void
  railOn: boolean
  /** Absent below 1180, where there is no room for the rail at all. */
  onRail?: () => void
  allCollapsed: boolean
  onCollapseAll: () => void
}) {
  const button = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  return (
    <div data-billing-toolbar="" className="flex shrink-0 items-center gap-2.5 bg-bg px-4 pt-4 xl:px-6">
      <label className={cn('flex h-[38px] w-[330px] items-center gap-2 rounded-[10px] border border-line',
        'bg-surface px-3 text-[13px] focus-within:border-accent-line focus-within:ring-2',
        'focus-within:ring-accent-ring')}>
        <IconSearch size={15} className="text-text-faint" />
        <input
          type="search"
          value={search}
          aria-label={SEARCH_BILLING_PLANS}
          placeholder={SEARCH_BILLING_PLANS}
          onChange={(event) => onSearch(event.target.value)}
          className="min-w-0 flex-1 border-none bg-transparent p-0 text-[13px] text-text outline-none
                     placeholder:text-text-faint focus-visible:shadow-none"
        />
      </label>

      {canEdit && (
        <button
          ref={button}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="flex h-[38px] shrink-0 items-center gap-[7px] rounded-[10px] border border-accent-line
                     bg-accent-soft px-3 text-[13px] font-semibold text-accent-dark hover:bg-accent-soft/70"
        >
          <IconPlus size={15} />
          {NEW_BILLING_PLAN}
          <IconChevronDown size={14} />
        </button>
      )}
      <NewPlanMenu
        open={open}
        anchor={button.current}
        onClose={() => setOpen(false)}
        onPick={onNewPlan}
      />

      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        {onRail && <Pill on={railOn} onClick={onRail}>Prices &amp; costs</Pill>}
        <Pill on={allCollapsed} onClick={onCollapseAll}>Collapse all</Pill>
      </div>
    </div>
  )
}

/**
 * Add visit ▾'s width (Board 10, 420), because Aaron asked for the same menu
 * at the same size — a menu, like Add visit's and the Move prompt, and so not
 * the builder's 480 popover. Named, like `MEASURE_WIDTH`, so a 420 in the
 * builder says why it is not 480.
 */
const NEW_PLAN_MENU_WIDTH = 420

/**
 * **New billing plan ▾** — Add visit ▾'s menu over the five plan kinds
 * (Aaron, review item 5.1). A 420px list hanging off the button, each row
 * `TileMenuRow`: a 28px tile wearing the kind's glyph — the one its card wears
 * under *How do you bill this?*, so a kind has one icon everywhere — the
 * kind's name and its one sentence. On a phone the same five rows are a
 * sheet.
 *
 * It was a `ContextMenu`, which is the ⋮ menu's look: no tiles, a different
 * width and different type from the Add visit menu one tab over.
 */
export function NewPlanMenu({
  open, anchor, onClose, onPick,
}: {
  open: boolean
  anchor: HTMLElement | null
  onClose: () => void
  onPick: (kind: BillingPlanKind) => void
}) {
  const phone = usePhone()
  const rows = BILLING_PLAN_KINDS.map((kind) => (
    <TileMenuRow
      key={kind}
      phone={phone}
      glyph={<KindGlyph kind={kind} />}
      label={BILLING_KIND_CARDS[kind].title}
      help={BILLING_KIND_CARDS[kind].sentence}
      onClick={() => { onClose(); onPick(kind) }}
    />
  ))

  if (phone) {
    return (
      <PanelSheet open={open} onClose={onClose} title={NEW_PLAN_SHEET.title} sub={NEW_PLAN_SHEET.sub} layer={70}>
        <div className="flex flex-col" data-new-plan-menu="">{rows}</div>
      </PanelSheet>
    )
  }

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} width={NEW_PLAN_MENU_WIDTH}
      label={NEW_PLAN_SHEET.title}
      className="overflow-hidden rounded-[12px] p-0">
      <div className="flex flex-col" data-new-plan-menu="">{rows}</div>
    </Popover>
  )
}
